import { useState, useEffect } from 'react';
import { MapPin, Clock, CheckCircle, Users, Zap, DollarSign, ShieldCheck } from 'lucide-react';

const Benefits = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('benefits');
      if (element) {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight * 0.75) {
          setIsVisible(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const benefits = [
    {
      title: "Hospital-Grade Staffing",
      description: "Our staffing model matches hospital reliability standards, eliminating call-outs and addressing caregiver shortages.",
      icon: ShieldCheck,
      delay: 100
    },
    {
      title: "Decentralized CareBases",
      description: "Our caregivers are based within 5-10 miles of clients for fast, reliable service.",
      icon: MapPin,
      delay: 200
    },
    {
      title: "Flexible, No-Minimum Visits",
      description: "Get exactly the care you need, when you need it — even if it's just 15 minutes.",
      icon: Clock,
      delay: 300
    },
    {
      title: "Forget Call-Outs and No-Shows",
      description: "With our redundant staffing model, we guarantee we'll always be there.",
      icon: CheckCircle,
      delay: 400
    },
    {
      title: "Dedicated Care Teams",
      description: "Receive consistent care from the same small team who knows your needs and preferences.",
      icon: Users,
      delay: 500
    },
    {
      title: "Tech-optimized Efficiency",
      description: "Our platform ensures seamless coordination and communication for better care.",
      icon: Zap,
      delay: 600
    },
    {
      title: "Well-Compensated Professionals",
      description: "Our caregivers earn significantly more than industry average, ensuring higher quality care.",
      icon: DollarSign,
      delay: 700
    }
  ];

  return (
    <section id="benefits" className="py-20 bg-slate-50">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold text-abbella-ebony mb-4 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            How We're Different
          </h2>
          <p className={`text-lg text-slate-600 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            FlowCare's innovative approach delivers benefits that traditional agencies simply can't match
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index} 
              className={`bg-white rounded-xl shadow-md p-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${benefit.delay}ms` }}
            >
              <div className="w-12 h-12 bg-abbella-aqua/10 rounded-lg flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-abbella-aqua" />
              </div>
              <h3 className="text-xl font-bold text-abbella-ebony mb-3">{benefit.title}</h3>
              <p className="text-slate-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Benefits;
